import React, { useCallback } from 'react';

import { Accordion } from '@radix-ui/react-accordion';

import {
    FilterCurrency,
    FilterShowHide,
    FilterSpam,
    FilterWallets,
    type FilterWalletsItem,
} from '@pkg/components/Sections/Profile/Filters';
import { type InputFloorPriceRangeOnChange } from '@pkg/components/Sections/Profile/Inputs';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { updateNFTManagerFilters } from '@app/store/actions/nft-manager';
import { type NFTManagerFilter } from '@app/store/types/nft-manager';
import { useWalletStore } from '@app/store/zustand';

import {
    type FilterCurrencyNFTContainerProps,
    type FilterSpamNFTContainerProps,
    type FilterWalletsNFTContainerProps,
} from './types';

const STORE_FILTERS_KEY = 'ownerAddress';

function FilterCurrencyNFTContainer({ index }: FilterCurrencyNFTContainerProps) {
    const dispatch = useAppDispatch();

    const floorPriceLe = useAppSelector((state) => state.nftManager.managerFilters.floorPriceLe);
    const floorPriceGe = useAppSelector((state) => state.nftManager.managerFilters.floorPriceGe);

    const updateQueryFilters = useCallback(
        (filters?: InputFloorPriceRangeOnChange) => {
            dispatch(
                updateNFTManagerFilters({
                    filter: filters,
                }),
            );
        },
        [dispatch],
    );

    return <FilterCurrency index={index} onChange={updateQueryFilters} initialState={{ floorPriceGe, floorPriceLe }} />;
}

function FilterWalletsNFTContainer({ index }: FilterWalletsNFTContainerProps) {
    const dispatch = useAppDispatch();

    const wallets = useWalletStore((state) => state.connectedWallets.map((item) => item.walletAddress));

    const filterCategory = useAppSelector((state) => state.nftManager.managerFilters[STORE_FILTERS_KEY]) || [];

    const updateQueryFilters = (filters?: NFTManagerFilter) => {
        dispatch(
            updateNFTManagerFilters({
                filter: filters,
            }),
        );
    };

    const handleOnItemClick = (title: FilterWalletsItem) => {
        const updated = filterCategory.includes(title)
            ? filterCategory.filter((s) => s !== title)
            : [...filterCategory, title];

        updateQueryFilters({
            [STORE_FILTERS_KEY]: updated,
        });
    };

    return (
        <FilterWallets index={index} items={wallets} itemsSelected={filterCategory} onItemClick={handleOnItemClick} />
    );
}

function FilterSpamNFTContainer({ index }: FilterSpamNFTContainerProps) {
    const dispatch = useAppDispatch();
    const spam = useAppSelector((state) => state.nftManager.managerFilters.spam);

    const updateQueryFilters = useCallback(
        (value: boolean) => {
            dispatch(
                updateNFTManagerFilters({
                    filter: { spam: value },
                }),
            );
        },
        [dispatch],
    );

    return <FilterSpam index={index} onChange={updateQueryFilters} state={!!spam} />;
}

function FilterShowHideNFTContainer({ index }: FilterSpamNFTContainerProps) {
    const dispatch = useAppDispatch();
    const spam = useAppSelector((state) => state.nftManager.managerFilters.spam);
    const unsupported = useAppSelector((state) => state.nftManager.managerFilters.unsupported);

    const updateQueryFilters = useCallback(
        (filter: string, value: boolean) => {
            dispatch(
                updateNFTManagerFilters({
                    filter: { [filter]: value },
                }),
            );
        },
        [dispatch],
    );

    return <FilterShowHide index={index} onChange={updateQueryFilters} spam={!!spam} unsupported={!!unsupported} />;
}

export function FiltersListAccordion() {
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    return (
        <Accordion id="nft-filters" type="multiple" defaultValue={['0', '1', '2']}>
            <FilterWalletsNFTContainer index={'2'} />
            {editingLiquidID ? <FilterShowHideNFTContainer index={'1'} /> : <FilterSpamNFTContainer index={'1'} />}
            <FilterCurrencyNFTContainer index={'0'} />
        </Accordion>
    );
}
